import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast, Toaster } from 'sonner';
import { CheckCircle2, AlertCircle, RefreshCw, Building2, Upload, X, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplaintForm = () => {
  const [businessInfo, setBusinessInfo] = useState(null);
  const [complaintSettings, setComplaintSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [complaintNumber, setComplaintNumber] = useState('');
  
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile_number: '',
    city: '',
    issue_type: '',
    notes: ''
  });
  
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Captcha state
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  const [captchaInput, setCaptchaInput] = useState('');

  useEffect(() => {
    fetchData();
    generateCaptcha();
  }, []);

  const fetchData = async () => {
    try {
      const [businessRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/business-settings/public`),
        axios.get(`${API_URL}/api/complaints/public/settings`)
      ]);
      setBusinessInfo(businessRes.data);
      setComplaintSettings(settingsRes.data);
    } catch (error) {
      // Use defaults if API fails
      setBusinessInfo({
        business_name: 'SUNTECH ENTERPRISES, Coimbatore',
        logo_url: null
      });
      setComplaintSettings({
        photo_upload_enabled: false,
        issue_types: [
          "Functional Error", "Device Not Working", "Configuration Issues",
          "Training Required", "Stamping Issue", "Broken Issues",
          "Software Issues", "Other Issues"
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: (num1 + num2).toString() });
    setCaptchaInput('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, JPEG, PNG files are allowed');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate fields
    if (!formData.customer_name || formData.customer_name.trim().length < 2) {
      toast.error('Please enter your name');
      return;
    }

    if (!formData.mobile_number || formData.mobile_number.length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    if (!formData.city || formData.city.trim().length < 2) {
      toast.error('Please enter your city');
      return;
    }

    if (!formData.issue_type) {
      toast.error('Please select an issue type');
      return;
    }

    // Validate captcha
    if (captchaInput !== captcha.answer) {
      toast.error('Incorrect captcha. Please try again.');
      generateCaptcha();
      return;
    }

    setSubmitting(true);
    try {
      // Use FormData for file upload
      const submitData = new FormData();
      submitData.append('customer_name', formData.customer_name);
      submitData.append('mobile_number', formData.mobile_number);
      submitData.append('city', formData.city);
      submitData.append('issue_type', formData.issue_type);
      submitData.append('notes', formData.notes || '');
      
      if (photo) {
        submitData.append('photo', photo);
      }

      const response = await axios.post(`${API_URL}/api/complaints/public/submit`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setComplaintNumber(response.data.complaint_number);
      setSubmitted(true);
      toast.success('Complaint submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit complaint. Please try again.');
      generateCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewComplaint = () => {
    setSubmitted(false);
    setComplaintNumber('');
    setFormData({
      customer_name: '',
      mobile_number: '',
      city: '',
      issue_type: '',
      notes: ''
    });
    setPhoto(null);
    setPhotoPreview(null);
    generateCaptcha();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 py-8 px-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-lg mx-auto">
        {/* Company Header */}
        <div className="text-center mb-6">
          {businessInfo?.logo_url ? (
            <img 
              src={businessInfo.logo_url} 
              alt="Company Logo" 
              className="h-20 mx-auto mb-3"
            />
          ) : (
            <div className="w-20 h-20 mx-auto mb-3 bg-blue-600 rounded-full flex items-center justify-center">
              <Building2 className="h-10 w-10 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            {businessInfo?.business_name || 'SUNTECH ENTERPRISES, Coimbatore'}
          </h1>
          <p className="text-gray-600 mt-1">Complaint Registration</p>
        </div>

        {submitted ? (
          /* Success Message */
          <Card className="shadow-lg border-0">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Complaint Registered!
              </h2>
              
              <div className="bg-gray-100 rounded-lg p-4 my-4">
                <p className="text-sm text-gray-600 mb-1">Your Complaint ID</p>
                <p className="text-2xl font-bold text-blue-600 font-mono">
                  {complaintNumber}
                </p>
              </div>
              
              <p className="text-gray-600 mb-6">
                Thank you for your submission. Our team member will call you back as soon as possible.
              </p>
              
              <p className="text-sm text-gray-500 mb-4">
                Please save your Complaint ID for future reference.
              </p>
              
              <Button onClick={handleNewComplaint} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Submit Another Complaint
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Complaint Form */
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                Register Your Complaint
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Customer Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mobile_number">Mobile Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="mobile_number"
                      type="tel"
                      value={formData.mobile_number}
                      onChange={(e) => handleChange('mobile_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit number"
                      maxLength={10}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="Your city"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="issue_type">Issue Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.issue_type}
                    onValueChange={(v) => handleChange('issue_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select issue type" />
                    </SelectTrigger>
                    <SelectContent>
                      {complaintSettings?.issue_types?.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="notes">Notes / Description</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Please describe your issue in detail..."
                    rows={4}
                  />
                </div>

                {/* Photo Upload - Only show if enabled */}
                {complaintSettings?.photo_upload_enabled && (
                  <div>
                    <Label>Attach Photo (Optional)</Label>
                    <p className="text-xs text-gray-500 mb-2">JPG, JPEG, PNG only. Max 2MB.</p>
                    
                    {!photoPreview ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handlePhotoChange}
                          className="hidden"
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload" className="cursor-pointer">
                          <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Click to upload photo</p>
                        </label>
                      </div>
                    ) : (
                      <div className="relative inline-block">
                        <img 
                          src={photoPreview} 
                          alt="Preview" 
                          className="max-h-40 rounded-lg border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          onClick={removePhoto}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Math Captcha */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <Label className="mb-2 block">
                    Security Check <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-3">
                    <div className="bg-white border rounded px-4 py-2 font-mono text-lg font-bold text-gray-700">
                      {captcha.num1} + {captcha.num2} = ?
                    </div>
                    <Input
                      type="number"
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value)}
                      placeholder="Answer"
                      className="w-24"
                      required
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={generateCaptcha}
                      title="Refresh captcha"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-lg"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Complaint'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          For urgent assistance, please call our support team.
        </p>
      </div>
    </div>
  );
};

export default ComplaintForm;
